import {AUTHCONST} from 'commons/auth/authConst';
import {getLocalStorageUserState} from 'commons/localStorage.utilities';
import {PublicRoutes} from 'navigation/routes';
import {useAuth} from 'contextApi/context/auth';
import {useLocation, useNavigate} from 'react-router-dom';
import {FC, useEffect} from 'react';

interface RoleFlagProps {
  isAdmin: boolean;
  isSupervisor: boolean;
  isPalowan: boolean;
}

interface RuleGuardProps {
  useRedirect?: boolean;
  renderForAdmin?: boolean;
  renderForPalowan?: boolean;
  renderForSupervisor?: boolean;
  hideForPalowan?: boolean;
  hideForAdmin?: boolean;
  hideForSupervisor?: boolean;
  getRole?: boolean;
  render?: (props: RoleFlagProps) => JSX.Element;
}

interface VisibilityConfig {
  hideForPalowan?: boolean;
  hideForAdmin?: boolean;
  hideForSupervisor?: boolean;
}

interface RenderConfig {
  renderForAdmin?: boolean;
  renderForPalowan?: boolean;
}

interface GuardRenderConfig extends RenderConfig {
  children: React.ReactNode;
  renderForSupervisor?: boolean;
  getRole?: boolean;
  render?: (props: RoleFlagProps) => JSX.Element;
  flags: RoleFlagProps;
  isHidden: boolean;
}

const getPrivateRoute = (rol: string) => {
  if (rol === AUTHCONST.ADMIN || rol === AUTHCONST.USER) {
    return '/private/palowan/leaves/dashboard';
  }
  return `/${PublicRoutes.LOGIN}`;
};

const isHiddenByRole = (config: VisibilityConfig, flags: RoleFlagProps) => {
  const hiddenScore =
    Number(!!config.hideForPalowan && flags.isPalowan) +
    Number(!!config.hideForSupervisor && flags.isSupervisor) +
    Number(!!config.hideForAdmin && flags.isAdmin);
  return hiddenScore > 0;
};

const canRenderChildren = (config: RenderConfig, flags: RoleFlagProps) => {
  const noSpecificRole = !config.renderForAdmin && !config.renderForPalowan;
  const adminMatch = !!config.renderForAdmin && flags.isAdmin;
  const palowanMatch = !!config.renderForPalowan && flags.isPalowan;
  return noSpecificRole || adminMatch || palowanMatch;
};

const resolveGuardRender = ({
  children,
  renderForSupervisor,
  getRole,
  render,
  flags,
  isHidden,
  renderForAdmin,
  renderForPalowan,
}: GuardRenderConfig) => {
  if (isHidden) {
    return null;
  }
  if (renderForSupervisor && flags.isSupervisor) {
    return children;
  }
  if (getRole && render) {
    return render(flags);
  }
  if (canRenderChildren({renderForAdmin, renderForPalowan}, flags)) {
    return children;
  }
  return null;
};

export const RoleGuard: FC<RuleGuardProps> = ({
  children,
  renderForAdmin,
  renderForPalowan,
  renderForSupervisor,
  hideForPalowan,
  hideForAdmin,
  hideForSupervisor,
  useRedirect,
  getRole,
  render,
}) => {
  const {role, userDetails} = useAuth();
  const persistedRole = getLocalStorageUserState(AUTHCONST.ROL);
  const isUser = persistedRole ?? role;
  const {isSupervisor} = userDetails?.data || {};
  const navigate = useNavigate();
  const {pathname} = useLocation();
  const rol = isUser.data[0];
  const isAdmin = rol === AUTHCONST.ADMIN;
  const isPalowan = rol === AUTHCONST.USER;
  const normalizedIsSupervisor = !!isSupervisor;
  const roleFlags: RoleFlagProps = {
    isAdmin,
    isPalowan,
    isSupervisor: normalizedIsSupervisor,
  };

  useEffect(() => {
    if (!useRedirect || pathname !== '/private') {
      return;
    }
    navigate(getPrivateRoute(rol));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const isHidden = isHiddenByRole(
    {hideForPalowan, hideForSupervisor, hideForAdmin},
    roleFlags
  );

  const content = resolveGuardRender({
    children,
    renderForSupervisor,
    getRole,
    render,
    flags: roleFlags,
    isHidden,
    renderForAdmin,
    renderForPalowan,
  });

  return <>{content}</>;
};

export default RoleGuard;
